import React, { useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Autocomplete, Box, Grid, MenuItem, TextField } from "@mui/material";
import { useTranslate } from "react-admin";
import {
  getAccountTemplateVariables,
  getAccountsTemplates,
  getMetaTemplateVariables,
  getMetaWhatsappTemplates,
  getProvidersByAccountId,
} from "../../services/testService";
import type {
  MetaTemplateVariable,
  MetaWhatsappTemplate,
  NotificationChannel,
  NotificationTestFormValues,
  TemplateOption,
  WhatsappHeaderMediaType,
} from "../../types/notificationTest";
import {
  extractPositionalVariables,
  getTemplateHeaderType,
} from "../../types/notificationTest";
import {
  CHANNEL_BACKEND_NAME,
  MESSAGE_TYPE_CHOICES,
  PROVIDER_TYPE,
} from "../../constants";
import VariablesArray from "./VariablesArray";
import MetaTemplateVariablesFields from "./MetaTemplateVariablesFields";
import TemplateHeaderMediaField from "./TemplateHeaderMediaField";
import CustomMessageMediaField from "./CustomMessageMediaField";
import RichTextEditor from "./RichTextEditor";
import AttachmentsPreview from "./AttachmentsPreview";
import { collectReferencedCids } from "../../transforms/rewriteEditorHtml";

interface MessageSectionProps {
  channel: NotificationChannel;
}

// Custom-content length caps per channel (single SMS segment, WhatsApp and
// Telegram body limits). Email has no cap (rich HTML).
const CONTENT_MAX: Partial<Record<NotificationChannel, number>> = {
  sms: 160,
  whatsapp: 4095,
  telegram: 4096,
};

export const MessageSection: React.FC<MessageSectionProps> = ({ channel }) => {
  const translate = useTranslate();
  const { control, setValue, getValues } =
    useFormContext<NotificationTestFormValues>();
  const messageType = useWatch({ control, name: "message.type" });
  const customMediaType = useWatch({ control, name: "message.customMedia.type" });
  const accountId = useWatch({ control, name: "accountId" });
  const appId = useWatch({ control, name: "appId" });

  const hideContentForAudio =
    channel === "whatsapp" &&
    messageType === "CUSTOM" &&
    customMediaType === "AUDIO";

  const pruneOrphanInlineAttachments = (html: string): void => {
    const referenced = collectReferencedCids(html);
    const current = getValues("attachments") ?? [];
    const pruned = current.filter((a) => {
      if (a.inline === true && a.contentId) {
        return referenced.has(a.contentId);
      }
      return true;
    });
    if (pruned.length !== current.length) {
      setValue("attachments", pruned, { shouldDirty: true });
    }
  };

  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [metaWhatsappTemplates, setMetaWhatsappTemplates] = useState<MetaWhatsappTemplate[]>([]);
  const [selectedMetaTemplateId, setSelectedMetaTemplateId] = useState<string>("");
  // WhatsApp test can send via the Meta template (business-initiated) OR a Templatify template
  // (session message), mirroring the account's real template-source selector.
  const [whatsappTemplateSource, setWhatsappTemplateSource] = useState<"META" | "TEMPLATIFY">("META");
  const [headerMediaType, setHeaderMediaType] =
    useState<WhatsappHeaderMediaType | null>(null);
  const [hasMetaWhatsappProvider, setHasMetaWhatsappProvider] = useState(false);
  const [metaBodyVariables, setMetaBodyVariables] = useState<
    MetaTemplateVariable[]
  >([]);
  const [metaHeaderTextVariables, setMetaHeaderTextVariables] = useState<
    MetaTemplateVariable[]
  >([]);

  const messageTypeChoices =
    channel === "email" ||
    channel === "sms" ||
    channel === "telegram" ||
    (channel === "whatsapp" && hasMetaWhatsappProvider)
      ? MESSAGE_TYPE_CHOICES
      : MESSAGE_TYPE_CHOICES.filter((c) => c.id === "CUSTOM");

  const handleTemplateChange = async (newTemplateId: string): Promise<void> => {
    const picked = templates.find((t) => t.id === newTemplateId);
    setValue("message.templateType", picked?.templateType ?? "", {
      shouldDirty: true,
    });
    setValue("message.language", picked?.language ?? "", {
      shouldDirty: true,
    });
    if (!newTemplateId || !accountId) return;
    try {
      const vars = await getAccountTemplateVariables({
        accountId,
        templateId: newTemplateId,
      });
      const seeded =
        vars.length > 0
          ? vars.map((v) => ({ key: v.variable_name, value: "" }))
          : [{ key: "", value: "" }];
      setValue("message.variables", seeded, { shouldDirty: true });
    } catch {}
  };

  // Apply a picked Meta template: set the id/language and load its header/body variables.
  const applyMetaTemplate = (newName: string): void => {
    setSelectedMetaTemplateId(newName);
    const picked = metaWhatsappTemplates.find((t) => (t.name ?? "") === newName);
    setValue("message.metaTemplateId", newName, { shouldDirty: true });
    setValue("message.templateId", "", { shouldDirty: false });
    setValue("message.language", picked?.language ?? "", { shouldDirty: true });

    setMetaBodyVariables([]);
    setMetaHeaderTextVariables([]);
    setValue("message.variables", [{ key: "", value: "" }], { shouldDirty: false });
    setValue("message.headerTextVariables", [], { shouldDirty: false });

    const headerKind = getTemplateHeaderType(picked);
    const nextHeaderType: WhatsappHeaderMediaType | null =
      headerKind === "IMAGE" || headerKind === "DOCUMENT" || headerKind === "VIDEO"
        ? headerKind
        : null;
    setHeaderMediaType(nextHeaderType);
    setValue(
      "message.headerMedia",
      { type: nextHeaderType ?? "", source: "URL", link: "", fileName: "", fileBase64: "", mimeType: "" },
      { shouldDirty: false },
    );

    if (!newName || !accountId) return;
    getMetaTemplateVariables({ accountId, name: newName, language: picked?.language ?? undefined })
      .then((res) => {
        const format = (res.parameterFormat ?? "").toUpperCase();
        if (format === "POSITIONAL") {
          const { headerVars, bodyVars } = extractPositionalVariables(picked);
          setMetaBodyVariables(bodyVars);
          setMetaHeaderTextVariables(headerVars);
          setValue("message.variables", bodyVars.map((v) => ({ key: v.paramName, value: "" })), { shouldDirty: true });
          setValue("message.headerTextVariables", headerVars.map((v) => ({ key: v.paramName, value: "" })), { shouldDirty: true });
          return;
        }
        if (format !== "NAMED" || res.variables.length === 0) {
          setMetaBodyVariables([]);
          setMetaHeaderTextVariables([]);
          return;
        }
        const bodyVars = res.variables.filter((v) => v.component === "BODY");
        const headerVars = res.variables.filter((v) => v.component === "HEADER");
        setMetaBodyVariables(bodyVars);
        setMetaHeaderTextVariables(headerVars);
        setValue("message.variables", bodyVars.map((v) => ({ key: v.paramName, value: "" })), { shouldDirty: true });
        setValue("message.headerTextVariables", headerVars.map((v) => ({ key: v.paramName, value: "" })), { shouldDirty: true });
      })
      .catch(() => {
        setMetaBodyVariables([]);
        setMetaHeaderTextVariables([]);
      });
  };

  // Switch the WhatsApp template source (Meta vs Templatify) and reset both sides so only the
  // chosen source's fields are submitted.
  const applyWhatsappSource = (next: "META" | "TEMPLATIFY"): void => {
    setWhatsappTemplateSource(next);
    setSelectedMetaTemplateId("");
    setMetaBodyVariables([]);
    setMetaHeaderTextVariables([]);
    setHeaderMediaType(null);
    setValue("message.metaTemplateId", "", { shouldDirty: false });
    setValue("message.templateId", "", { shouldDirty: false });
    setValue("message.templateType", "", { shouldDirty: false });
    setValue("message.language", "", { shouldDirty: false });
    setValue("message.variables", [{ key: "", value: "" }], { shouldDirty: false });
    setValue("message.headerTextVariables", [], { shouldDirty: false });
    setValue(
      "message.headerMedia",
      { type: "", source: "URL", link: "", fileName: "", fileBase64: "", mimeType: "" },
      { shouldDirty: false },
    );
  };

  useEffect(() => {
    if (!accountId || !appId) {
      setTemplates([]);
      return;
    }
    let cancelled = false;
    getAccountsTemplates({
      accountId,
      application: appId,
      channel: CHANNEL_BACKEND_NAME[channel],
    })
      .then((res) => {
        if (!cancelled) setTemplates(res);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, appId, channel]);

  useEffect(() => {
    if (channel !== "whatsapp" || !accountId) {
      setHasMetaWhatsappProvider(false);
      return;
    }
    let cancelled = false;
    getProvidersByAccountId(accountId)
      .then((providers) => {
        if (cancelled) return;
        setHasMetaWhatsappProvider(
          providers.some((p) => p.providerType === PROVIDER_TYPE.MetaWhatsapp),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setHasMetaWhatsappProvider(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, channel]);

  useEffect(() => {
    if (channel !== "whatsapp" || messageType !== "TEMPLATE" || !accountId) {
      setMetaWhatsappTemplates([]);
      setSelectedMetaTemplateId("");
      setMetaBodyVariables([]);
      setMetaHeaderTextVariables([]);
      setHeaderMediaType(null);
      setValue("message.metaTemplateId", "", { shouldDirty: false });
      setValue("message.language", "", { shouldDirty: false });
      setValue("message.variables", [{ key: "", value: "" }], {
        shouldDirty: false,
      });
      setValue("message.headerTextVariables", [], { shouldDirty: false });
      setValue(
        "message.headerMedia",
        {
          type: "",
          source: "URL",
          link: "",
          fileName: "",
          fileBase64: "",
          mimeType: "",
        },
        { shouldDirty: false },
      );
      return;
    }
    let cancelled = false;
    getMetaWhatsappTemplates({ accountId })
      .then((res) => {
        if (!cancelled) setMetaWhatsappTemplates(res);
      })
      .catch(() => {
        if (!cancelled) setMetaWhatsappTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, channel, messageType]);

  useEffect(() => {
    if (hideContentForAudio) {
      setValue("message.content", "", { shouldDirty: false });
    }
  }, [hideContentForAudio, setValue]);

  useEffect(() => {
    if (
      channel === "whatsapp" &&
      messageType === "CUSTOM" &&
      hasMetaWhatsappProvider
    ) {
      return;
    }
    setValue(
      "message.customMedia",
      {
        type: "",
        source: "URL",
        link: "",
        fileName: "",
        fileBase64: "",
        mimeType: "",
      },
      { shouldDirty: false },
    );
  }, [channel, messageType, hasMetaWhatsappProvider, setValue]);

  return (
    <>
      <Controller
        name="message.type"
        control={control}
        rules={{
          required: translate("resources.test.validation.messageTypeRequired"),
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            select
            fullWidth
            required
            sx={{ mb: 2 }}
            label={translate("resources.test.fields.messageType")}
            InputLabelProps={{ shrink: true }}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? " "}
          >
            {messageTypeChoices.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {translate(c.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      {channel === "whatsapp" && messageType === "TEMPLATE" && (
        <>
          <TextField
            select
            fullWidth
            sx={{ mb: 2 }}
            label={translate("resources.test.fields.whatsappTemplateSource")}
            InputLabelProps={{ shrink: true }}
            value={whatsappTemplateSource}
            onChange={(e) =>
              applyWhatsappSource(e.target.value as "META" | "TEMPLATIFY")
            }
          >
            <MenuItem value="META">Meta</MenuItem>
            <MenuItem value="TEMPLATIFY">Templatify</MenuItem>
          </TextField>

          {whatsappTemplateSource === "META" && (
            <Autocomplete
              fullWidth
              sx={{ mb: 2 }}
              options={metaWhatsappTemplates}
              getOptionLabel={(t) =>
                `${t.name ?? "(unnamed)"}${t.language ? ` — ${t.language}` : ""}${
                  t.status ? ` (${t.status})` : ""
                }`
              }
              isOptionEqualToValue={(o, v) => (o.name ?? "") === (v.name ?? "")}
              value={
                metaWhatsappTemplates.find(
                  (t) => (t.name ?? "") === selectedMetaTemplateId,
                ) ?? null
              }
              onChange={(_e, picked) => applyMetaTemplate(picked?.name ?? "")}
              disabled={!accountId || metaWhatsappTemplates.length === 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate("resources.test.fields.whatsappMetaTemplate")}
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    !accountId
                      ? translate(
                          "resources.test.fields.whatsappMetaTemplateHintNoAccount",
                        )
                      : metaWhatsappTemplates.length === 0
                        ? translate(
                            "resources.test.fields.whatsappMetaTemplateHintEmpty",
                          )
                        : " "
                  }
                />
              )}
            />
          )}

          {whatsappTemplateSource === "TEMPLATIFY" && (
            <>
              <Controller
                name="message.templateId"
                control={control}
                rules={{
                  required: translate(
                    "resources.test.validation.templateRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    fullWidth
                    sx={{ mb: 2 }}
                    options={templates}
                    getOptionLabel={(t) =>
                      `${t.templateName}${t.language ? ` - ${t.language}` : ""}`
                    }
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={templates.find((t) => t.id === field.value) ?? null}
                    onChange={(_e, picked) => {
                      field.onChange(picked?.id ?? "");
                      void handleTemplateChange(picked?.id ?? "");
                    }}
                    disabled={!accountId || templates.length === 0}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={translate("resources.test.fields.template")}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={
                          !accountId
                            ? translate(
                                "resources.test.fields.whatsappMetaTemplateHintNoAccount",
                              )
                            : templates.length === 0
                              ? translate(
                                  "resources.test.fields.whatsappMetaTemplateHintEmpty",
                                )
                              : " "
                        }
                      />
                    )}
                  />
                )}
              />
              <VariablesArray />
            </>
          )}

          {whatsappTemplateSource === "META" &&
            selectedMetaTemplateId &&
            headerMediaType !== null && (
              <TemplateHeaderMediaField headerType={headerMediaType} />
            )}

          {whatsappTemplateSource === "META" &&
            selectedMetaTemplateId &&
            metaHeaderTextVariables.length > 0 && (
              <MetaTemplateVariablesFields
                variables={metaHeaderTextVariables}
                field="headerTextVariables"
                title="Header variables"
              />
            )}

          {whatsappTemplateSource === "META" &&
            selectedMetaTemplateId &&
            metaBodyVariables.length > 0 && (
              <MetaTemplateVariablesFields variables={metaBodyVariables} />
            )}
        </>
      )}

      {messageType === "TEMPLATE" && channel !== "whatsapp" && (
        <Box>
          <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="message.templateId"
                control={control}
                rules={{
                  required: translate(
                    "resources.test.validation.templateRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    required
                    label={translate("resources.test.fields.template")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    onChange={(e) => {
                      field.onChange(e);
                      void handleTemplateChange(String(e.target.value));
                    }}
                  >
                    {templates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.templateName}
                        {t.language ? ` - ${t.language}` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="message.language"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.fields.language")}
                    placeholder={translate("resources.test.fields.language")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <VariablesArray />
        </Box>
      )}

      {messageType === "CUSTOM" && (
        <Box>
          {channel === "whatsapp" && hasMetaWhatsappProvider && (
            <CustomMessageMediaField />
          )}
          <Grid container spacing={{ xs: 1.5, sm: 3 }}>
            {channel === "email" && (
              <Grid item xs={12}>
                <Controller
                  name="message.subject"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={translate("resources.test.fields.subject")}
                      placeholder={translate("resources.test.fields.subject")}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            )}
            {!hideContentForAudio && (
            <Grid item xs={12}>
              <Controller
                name="message.content"
                control={control}
                rules={{
                  validate: (v) => {
                    const html = v ?? "";
                    if (channel !== "email") {
                      if (channel === "whatsapp") {
                        const media = getValues("message.customMedia");
                        const hasMedia =
                          !!media?.type &&
                          ((media.link ?? "").trim() !== "" ||
                            (media.fileBase64 ?? "") !== "");
                        if (hasMedia) return true;
                      }
                      return (
                        html.trim().length > 0 ||
                        translate("resources.test.validation.contentRequired")
                      );
                    }
                    if (/<img\s/i.test(html)) return true;
                    const text = html.replace(/<[^>]+>/g, "").trim();
                    return (
                      text.length > 0 ||
                      translate("resources.test.validation.contentRequired")
                    );
                  },
                }}
                render={({ field, fieldState }) =>
                  channel === "email" ? (
                    <RichTextEditor
                      value={field.value ?? ""}
                      onChange={(html) => {
                        field.onChange(html);
                        pruneOrphanInlineAttachments(html);
                      }}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      label={translate("resources.test.fields.content")}
                      required
                    />
                  ) : (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      multiline
                      minRows={4}
                      label={translate("resources.test.fields.content")}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        maxLength: CONTENT_MAX[channel],
                      }}
                      error={Boolean(fieldState.error)}
                      helperText={
                        fieldState.error?.message ??
                        (CONTENT_MAX[channel]
                          ? `${(field.value ?? "").length}/${CONTENT_MAX[channel]}`
                          : undefined)
                      }
                      FormHelperTextProps={
                        fieldState.error
                          ? undefined
                          : {
                              className: "rg-mono",
                              sx: { textAlign: "right", mr: 0 },
                            }
                      }
                    />
                  )
                }
              />
            </Grid>
            )}
            {channel === "email" && (
              <Grid item xs={12}>
                <AttachmentsPreview />
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </>
  );
};

export default MessageSection;
